import { useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import DashboardLayout from "../components/layout/DashboardLayout";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { isCrmPlatformAuthorityEnabled } from "../lib/crmAdmin";
import { 
  Upload, 
  FileText, 
  Users, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Download,
  Smartphone,
  Mail,
  RefreshCw,
  UserPlus,
  Filter
} from "lucide-react";

const ContactImportPage = () => {
  const { user } = useAuth();
  // STARSEED-CRM-012: while the platform CRM is authoritative
  // (crm_platform_authority ON), the legacy import — which writes Mongo
  // managed_leads — is paused so a frozen collection is never written.
  // Import lands in the platform CRM with STARSEED-CRM-017.
  const platformCrm = isCrmPlatformAuthorityEnabled(user);
  const [activeTab, setActiveTab] = useState("csv");
  const [fileContent, setFileContent] = useState("");
  const [parsedContacts, setParsedContacts] = useState([]);
  const [duplicateResults, setDuplicateResults] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [showImportModal, setShowImportModal] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  // Check for duplicates (declared before `handleFileUpload` so the upload
  // handler can include it in its useCallback dep list without a use-before-
  // declare hazard).
  const checkDuplicates = useCallback(async (contacts = parsedContacts) => {
    if (contacts.length === 0) {
      toast.error("No contacts to check");
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post("/contacts/check-duplicates", contacts);
      setDuplicateResults(res.data);
      
      // Update selection to exclude duplicates
      const newSelected = new Set();
      res.data.results.forEach((r, i) => {
        if (!r.is_duplicate) {
          newSelected.add(i);
        }
      });
      setSelectedContacts(newSelected);
      
      toast.info(`Found ${res.data.duplicates_found} duplicates out of ${res.data.total_checked} contacts`);
    } catch (error) {
      toast.error("Failed to check duplicates");
    } finally {
      setLoading(false);
    }
  }, [parsedContacts]);

  // Handle file upload
  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target?.result;
      setFileContent(content);

      // Parse based on file type
      const isVCard = file.name.endsWith(".vcf") || file.name.endsWith(".vcard");

      setLoading(true);
      try {
        const endpoint = isVCard ? "/contacts/parse-vcard" : "/contacts/parse-csv";
        const res = await api.post(endpoint, content, {
          headers: { "Content-Type": "text/plain" }
        });

        setParsedContacts(res.data.contacts || []);
        setSelectedContacts(new Set(res.data.contacts?.map((_, i) => i) || []));
        toast.success(`Parsed ${res.data.total_parsed} contacts from ${file.name}`);

        // Auto-check for duplicates
        if (res.data.contacts?.length > 0) {
          checkDuplicates(res.data.contacts);
        }
      } catch (error) {
        toast.error("Failed to parse file");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(file);
  }, [checkDuplicates]);

  // Import selected contacts
  const importContacts = useCallback(async () => {
    const contactsToImport = parsedContacts.filter((_, i) => selectedContacts.has(i));
    
    if (contactsToImport.length === 0) {
      toast.error("No contacts selected for import");
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post("/contacts/import", {
        contacts: contactsToImport,
        source: activeTab === "vcard" ? "ios" : activeTab,
        skip_duplicates: skipDuplicates,
        auto_create_leads: true
      });
      
      setImportResults(res.data);
      setShowImportModal(true);
      
      if (res.data.imported_count > 0) {
        toast.success(`Successfully imported ${res.data.imported_count} contacts as leads`);
      }
    } catch (error) {
      toast.error("Import failed");
    } finally {
      setLoading(false);
    }
  }, [parsedContacts, selectedContacts, activeTab, skipDuplicates]);

  // Toggle contact selection
  const toggleContact = (index) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedContacts(newSelected);
  };

  // Select all/none
  const selectAll = () => {
    if (selectedContacts.size === parsedContacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(parsedContacts.map((_, i) => i)));
    }
  };

  // Select only non-duplicates
  const selectNonDuplicates = () => {
    if (!duplicateResults) return;
    const newSelected = new Set();
    duplicateResults.results.forEach((r, i) => {
      if (!r.is_duplicate) {
        newSelected.add(i);
      }
    });
    setSelectedContacts(newSelected);
  };

  // Manual entry
  const [manualContacts, setManualContacts] = useState("");
  
  const parseManualContacts = useCallback(() => {
    const lines = manualContacts.split("\n").filter(l => l.trim());
    const contacts = lines.map(line => {
      // Try to parse "Name <email>" or "email" format
      const emailMatch = line.match(/<([^>]+)>/) || line.match(/([^\s,]+@[^\s,]+)/);
      const email = emailMatch ? emailMatch[1].trim().toLowerCase() : null;
      
      let name = line.replace(/<[^>]+>/, "").replace(/[^\s,]+@[^\s,]+/, "").trim();
      name = name.replace(/[,<>]/g, "").trim();
      
      return { email, name: name || null };
    }).filter(c => c.email);
    
    setParsedContacts(contacts);
    setSelectedContacts(new Set(contacts.map((_, i) => i)));
    
    if (contacts.length > 0) {
      checkDuplicates(contacts);
    }
    
    toast.success(`Parsed ${contacts.length} contacts`);
  }, [manualContacts, checkDuplicates]);

  // Download template
  const downloadTemplate = () => {
    const csv = "Name,Email,Phone,Company\nJohn Doe,john@example.com,+1234567890,Acme Corp\nJane Smith,jane@example.com,+0987654321,Tech Inc";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contact_import_template.csv";
    a.click();
    toast.success("Template downloaded");
  };

  return (
    <DashboardLayout 
      title="Import Contacts" 
      subtitle="Import contacts from iOS, Google, or CSV to avoid duplicate outreach"
    >
      {platformCrm && (
        <Card className="bg-yellow-500/10 border-yellow-500/30 mb-6" data-testid="crm-import-paused-banner">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="text-yellow-500 shrink-0" size={20} />
            <div>
              <p className="text-yellow-400 font-medium">Contact import is paused during the EchoSense CRM transition</p>
              <p className="text-yellow-400/70 text-sm">
                The platform CRM is now authoritative; the legacy import writes the retired lead store.
                Parsing and duplicate checks still work — importing resumes when import lands in
                EchoSense CRM (STARSEED-CRM-017).
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Import Options */}
        <Card className="bg-surface border-platinum/10">
          <CardHeader>
            <CardTitle className="text-white font-serif">Import Source</CardTitle>
            <CardDescription>Choose how to import your contacts</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-3 bg-surface-highlight">
                <TabsTrigger value="csv">CSV</TabsTrigger>
                <TabsTrigger value="vcard">iOS/vCard</TabsTrigger>
                <TabsTrigger value="manual">Manual</TabsTrigger>
              </TabsList>
              
              <TabsContent value="csv" className="space-y-4 mt-4">
                <div className="p-4 border-2 border-dashed border-platinum/20 rounded-lg text-center">
                  <Upload className="mx-auto mb-2 text-gray-400" size={32} />
                  <p className="text-gray-400 text-sm mb-2">Upload CSV file</p>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label htmlFor="csv-upload">
                    <Button variant="outline" className="border-platinum/20" asChild>
                      <span>Select CSV File</span>
                    </Button>
                  </label>
                </div>
                <Button variant="link" onClick={downloadTemplate} className="text-burgundy p-0">
                  <Download size={14} className="mr-1" />
                  Download CSV Template
                </Button>
                <div className="text-xs text-gray-500">
                  <p className="font-medium mb-1">Supported columns:</p>
                  <p>Name, Email, Phone, Company</p>
                  <p className="mt-1">Also supports: First Name, Last Name, Mobile, Organization</p>
                </div>
              </TabsContent>
              
              <TabsContent value="vcard" className="space-y-4 mt-4">
                <div className="p-4 border-2 border-dashed border-platinum/20 rounded-lg text-center">
                  <Smartphone className="mx-auto mb-2 text-gray-400" size={32} />
                  <p className="text-gray-400 text-sm mb-2">Upload vCard (.vcf) file</p>
                  <Input
                    type="file"
                    accept=".vcf,.vcard"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="vcard-upload"
                  />
                  <label htmlFor="vcard-upload">
                    <Button variant="outline" className="border-platinum/20" asChild>
                      <span>Select vCard File</span>
                    </Button>
                  </label>
                </div>
                <div className="text-xs text-gray-500 bg-surface-highlight p-3 rounded">
                  <p className="font-medium mb-2">How to export from iOS:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Open Contacts app</li>
                    <li>Select contacts or "All Contacts"</li>
                    <li>Tap Share → AirDrop/Email</li>
                    <li>Save the .vcf file</li>
                  </ol>
                </div>
              </TabsContent>
              
              <TabsContent value="manual" className="space-y-4 mt-4">
                <div>
                  <Label className="text-gray-300 text-sm">Paste contacts (one per line)</Label>
                  <Textarea
                    value={manualContacts}
                    onChange={(e) => setManualContacts(e.target.value)}
                    placeholder="John Doe <john@example.com>
jane@example.com
Bob Smith bob@company.com"
                    className="bg-surface-highlight border-platinum/20 mt-1 h-32"
                  />
                </div>
                <Button onClick={parseManualContacts} className="w-full bg-burgundy hover:bg-burgundy-light">
                  Parse Contacts
                </Button>
                <p className="text-xs text-gray-500">
                  Formats: "Name &lt;email&gt;" or just "email@domain.com"
                </p>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Parsed Contacts */}
        <Card className="lg:col-span-2 bg-surface border-platinum/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white font-serif">
                  Contacts ({parsedContacts.length})
                </CardTitle>
                <CardDescription>
                  {selectedContacts.size} selected for import
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {parsedContacts.length > 0 && (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => checkDuplicates()}
                      className="border-platinum/20"
                      disabled={loading}
                    >
                      <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
                      Check Duplicates
                    </Button>
                    <Button
                      size="sm"
                      onClick={importContacts}
                      className="bg-burgundy hover:bg-burgundy-light"
                      disabled={loading || selectedContacts.size === 0 || platformCrm}
                      title={platformCrm ? "Import is paused while EchoSense CRM is authoritative (STARSEED-CRM-017)" : undefined}
                    >
                      <UserPlus size={14} className="mr-1" />
                      Import Selected
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Selection controls */}
            {parsedContacts.length > 0 && (
              <div className="flex items-center gap-4 mb-4 pb-4 border-b border-platinum/10">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={selectedContacts.size === parsedContacts.length}
                    onCheckedChange={selectAll}
                  />
                  <span className="text-sm text-gray-400">Select All</span>
                </div>
                {duplicateResults && duplicateResults.duplicates_found > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={selectNonDuplicates}
                    className="border-platinum/20"
                  >
                    <Filter size={14} className="mr-1" />
                    Select Non-Duplicates Only
                  </Button>
                )}
                <div className="flex items-center gap-2 ml-auto">
                  <Checkbox 
                    id="skip-dupes"
                    checked={skipDuplicates}
                    onCheckedChange={setSkipDuplicates}
                  />
                  <Label htmlFor="skip-dupes" className="text-sm text-gray-400">Skip duplicates on import</Label>
                </div>
              </div>
            )}

            {/* Duplicate stats */}
            {duplicateResults && (
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-surface-highlight rounded p-3 border border-platinum/10">
                  <p className="text-gray-400 text-xs">Total Checked</p>
                  <p className="text-white text-xl font-semibold">{duplicateResults.total_checked}</p>
                </div>
                <div className="bg-surface-highlight rounded p-3 border border-emerald-500/20">
                  <p className="text-gray-400 text-xs">New Contacts</p>
                  <p className="text-emerald-400 text-xl font-semibold">{duplicateResults.new_contacts}</p>
                </div>
                <div className="bg-surface-highlight rounded p-3 border border-yellow-500/20">
                  <p className="text-gray-400 text-xs">Duplicates Found</p>
                  <p className="text-yellow-400 text-xl font-semibold">{duplicateResults.duplicates_found}</p>
                </div>
              </div>
            )}

            {/* Contact list */}
            <div className="max-h-[400px] overflow-y-auto space-y-2">
              {parsedContacts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="mx-auto mb-3 opacity-50" size={40} />
                  <p>No contacts loaded</p>
                  <p className="text-sm">Upload a file or paste contacts manually</p>
                </div>
              ) : (
                parsedContacts.map((contact, index) => {
                  const dupeInfo = duplicateResults?.results?.[index];
                  const isDupe = dupeInfo?.is_duplicate;
                  
                  return (
                    <div 
                      key={`${contact.email || contact.name || 'row'}-${index}`}
                      className={`flex items-center gap-3 p-3 rounded border ${
                        isDupe 
                          ? 'bg-yellow-500/5 border-yellow-500/20' 
                          : selectedContacts.has(index)
                          ? 'bg-white/5 border-burgundy/30'
                          : 'bg-surface-highlight border-platinum/10'
                      }`}
                    >
                      <Checkbox 
                        checked={selectedContacts.has(index)}
                        onCheckedChange={() => toggleContact(index)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium truncate">
                            {contact.name || 'No name'}
                          </p>
                          {isDupe && (
                            <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                              Duplicate
                            </Badge>
                          )}
                        </div>
                        <p className="text-gray-400 text-sm truncate">{contact.email}</p>
                        {contact.phone && (
                          <p className="text-gray-500 text-xs">{contact.phone}</p>
                        )}
                      </div>
                      {isDupe && dupeInfo && (
                        <div className="text-right text-xs">
                          <p className="text-yellow-400">
                            {dupeInfo.match_type === 'lead' ? 'Existing Lead' : 'Existing User'}
                          </p>
                          {dupeInfo.existing_lead_status && (
                            <p className="text-gray-500">{dupeInfo.existing_lead_status}</p>
                          )}
                        </div>
                      )}
                      {!isDupe && (
                        <CheckCircle className="text-emerald-400" size={18} />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Import Results Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="bg-surface border-platinum/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-cream">Import Complete</DialogTitle>
            <DialogDescription className="text-platinum-dark">
              Contact import results
            </DialogDescription>
          </DialogHeader>
          
          {importResults && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-500/10 rounded p-3 text-center border border-emerald-500/20">
                  <CheckCircle className="mx-auto mb-1 text-emerald" size={24} />
                  <p className="text-cream text-2xl font-bold">{importResults.imported_count}</p>
                  <p className="text-platinum-dark text-xs">Imported</p>
                </div>
                <div className="bg-yellow-500/10 rounded p-3 text-center border border-yellow-500/20">
                  <AlertTriangle className="mx-auto mb-1 text-gold-dark" size={24} />
                  <p className="text-cream text-2xl font-bold">{importResults.skipped_count}</p>
                  <p className="text-platinum-dark text-xs">Skipped</p>
                </div>
                <div className="bg-red-500/10 rounded p-3 text-center border border-red-500/20">
                  <XCircle className="mx-auto mb-1 text-red-600" size={24} />
                  <p className="text-cream text-2xl font-bold">{importResults.error_count}</p>
                  <p className="text-platinum-dark text-xs">Errors</p>
                </div>
              </div>
              
              {importResults.imported?.length > 0 && (
                <div>
                  <p className="text-platinum-dark text-sm mb-2">Successfully imported:</p>
                  <div className="max-h-32 overflow-y-auto bg-surface-highlight rounded p-2 space-y-1">
                    {importResults.imported.slice(0, 10).map((c, i) => (
                      <p key={`imp-${c.email || c.name || i}`} className="text-platinum text-sm">
                        ✓ {c.name || c.email}
                      </p>
                    ))}
                    {importResults.imported.length > 10 && (
                      <p className="text-platinum-dark text-xs">
                        +{importResults.imported.length - 10} more...
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {importResults.skipped?.length > 0 && (
                <div>
                  <p className="text-platinum-dark text-sm mb-2">Skipped (duplicates):</p>
                  <div className="max-h-32 overflow-y-auto bg-surface-highlight rounded p-2 space-y-1">
                    {importResults.skipped.slice(0, 5).map((c, i) => (
                      <p key={`skip-${c.email || i}`} className="text-gold-dark text-sm">
                        ⚠ {c.email} - {c.reason}
                      </p>
                    ))}
                    {importResults.skipped.length > 5 && (
                      <p className="text-platinum-dark text-xs">
                        +{importResults.skipped.length - 5} more...
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button 
              onClick={() => {
                setShowImportModal(false);
                setParsedContacts([]);
                setDuplicateResults(null);
                setImportResults(null);
                setFileContent("");
              }} 
              className="bg-burgundy hover:bg-burgundy-light"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default ContactImportPage;
